import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import useIsPhone from '../utils/useIsPhone';
import VocationWorksLogo from '../components/VocationWorksLogo';
import ResourceTabs, { DIRECTORY_CONTAINER, DirectoryHeading } from '../components/ResourceTabs';
import {
  fetchPublishedWageRates, groupByFamily, shortLocalName, localDescriptorKey, localSummary,
  agreementsForLocal, defaultAgreement, sectionRows, classRange, IDOL_AGREEMENT, fmtRate, fmtPct, fmtDate, distinct,
} from '../utils/unionWages';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const ERROR_TEXT = '#F09595';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

function useViewportWidth() {
  const [width, setWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

const PREVIEW_LOCALS = 3; // local rows shown on a landing card before "+N more"
const ROW_HEIGHT = '56px'; // every local row, so rows line up across a grid row
const TRADES_SLOT = '2.1rem'; // fixed two-line slot for a family's trade names (blank if none)

const muted = { fontSize: '0.84rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', padding: '2.5rem 0' };
const small = { fontSize: '0.74rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 };
const linkStyle = { color: AMBER, textDecoration: 'underline', wordBreak: 'break-all' };
// Phone overrides (<= 640px): body text at least 14px, helper text lighter for contrast, and
// 44px tap targets. Desktop/tablet styles above are untouched.
const PHONE_TEXT = '0.875rem';
const PHONE_HELPER = 'rgba(255,255,255,0.68)';
const smallFor = isPhone => (isPhone ? { ...small, fontSize: PHONE_TEXT, color: PHONE_HELPER } : small);
const backBtnStyle = isPhone => ({
  background: 'none', border: 'none', padding: 0, color: AMBER, fontSize: isPhone ? '0.95rem' : '0.84rem', fontWeight: 700,
  fontFamily: FONT_BODY, cursor: 'pointer', ...(isPhone ? { minHeight: '44px', display: 'inline-flex', alignItems: 'center' } : {}),
});
const selStyle = {
  padding: '9px 12px', fontSize: '0.84rem', backgroundColor: NAVY_MID,
  border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', cursor: 'pointer', fontFamily: FONT_BODY, maxWidth: '100%',
};

const BENEFITS = [
  { key: 'health_welfare', labelKey: 'unionWages.healthWelfare' },
  { key: 'pension',        labelKey: 'unionWages.pension' },
  { key: 'annuity',        labelKey: 'unionWages.annuity' },
  { key: 'vacation',       labelKey: 'unionWages.vacation' },
];

// "Benefits paid by employer" line plus the total package figures, for one row. Renders
// nothing when the sheet printed none of them for that row. Total package is labelled as
// wage + employer-paid benefits, never as pay.
function BenefitsBlock({ row, compact }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const parts = BENEFITS.filter(b => fmtRate(row[b.key])).map(b => `${t(b.labelKey)} ${fmtRate(row[b.key])}`);
  if (row.other_fringes) parts.push(`${t('unionWages.otherFringes')}: ${row.other_fringes}`);
  const pkg = fmtRate(row.total_package);
  const payroll = fmtRate(row.total_payroll_package);
  if (parts.length === 0 && !pkg && !payroll) return null;
  const size = isPhone ? PHONE_TEXT : compact ? '0.72rem' : '0.8rem';
  return (
    <div style={{ marginTop: compact ? '4px' : '10px' }}>
      {parts.length > 0 && (
        <p style={{ fontSize: size, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>{t('unionWages.benefitsLabel')}: </span>
          {parts.join(' · ')}
        </p>
      )}
      {(pkg || payroll) && (
        <p style={{ fontSize: size, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, marginTop: '2px' }}>
          {pkg && <span>{t('unionWages.totalPackage')}: <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{pkg}</strong></span>}
          {pkg && payroll && ' · '}
          {payroll && <span>{t('unionWages.totalPayrollPackage')}: <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{payroll}</strong></span>}
        </p>
      )}
    </div>
  );
}

function RateTable({ title, rows, showPct, isMobile, skipBenefitsFor }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  if (rows.length === 0) return null;
  const wageText = r => (fmtRate(r.hourly_wage) ? `${r.is_estimate ? '~' : ''}${fmtRate(r.hourly_wage)}` : '—');
  if (isPhone) {
    // Phones: a stacked list instead of a 3-column table, which pushed the wage column off-screen.
    // Classification and hourly wage share the first line; % of journeyman and the benefits
    // breakdown get the full width underneath.
    return (
      <div style={{ marginTop: '18px' }}>
        <p style={{ fontFamily: FONT_HEAD, fontSize: '1.15rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '6px' }}>{title}</p>
        <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px' }}>
          {rows.map((r, i) => (
            <div key={`${r.sort_order}-${r.level}`} data-level={r.level} style={{ padding: '12px', borderTop: i ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.4, minWidth: 0 }}>{r.level}</p>
                <p data-wage style={{ fontSize: '1.05rem', fontWeight: 700, color: WHITE, whiteSpace: 'nowrap', lineHeight: 1.35 }}>{wageText(r)}</p>
              </div>
              {showPct && fmtPct(r.pct_of_journeyman) && (
                <p data-pct style={{ fontSize: PHONE_TEXT, color: PHONE_HELPER, marginTop: '2px' }}>
                  {t('unionWages.pctOfJourneyman', { pct: fmtPct(r.pct_of_journeyman) })}
                </p>
              )}
              {r !== skipBenefitsFor && <BenefitsBlock row={r} compact />}
            </div>
          ))}
        </div>
      </div>
    );
  }
  const cell = { padding: isMobile ? '9px 8px' : '10px 12px', borderTop: '1px solid rgba(255,255,255,0.07)', verticalAlign: 'top' };
  const head = { ...cell, borderTop: 'none', fontSize: '0.66rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'left' };
  return (
    <div style={{ marginTop: '18px' }}>
      <p style={{ fontFamily: FONT_HEAD, fontSize: '1.05rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '6px' }}>{title}</p>
      <div style={{ overflowX: 'auto', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT_BODY }}>
          <thead>
            <tr>
              <th style={head}>{t('unionWages.colClassification')}</th>
              {showPct && <th style={{ ...head, textAlign: 'right', whiteSpace: 'nowrap' }}>{t('unionWages.colPct')}</th>}
              <th style={{ ...head, textAlign: 'right', whiteSpace: 'nowrap' }}>{t('unionWages.colWage')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={`${r.sort_order}-${r.level}`} data-level={r.level}>
                <td style={{ ...cell, fontSize: '0.84rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>
                  {r.level}
                  {r !== skipBenefitsFor && <BenefitsBlock row={r} compact />}
                </td>
                {showPct && (
                  <td style={{ ...cell, textAlign: 'right', fontSize: '0.84rem', color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>
                    {fmtPct(r.pct_of_journeyman) || '—'}
                  </td>
                )}
                <td data-wage style={{ ...cell, textAlign: 'right', fontSize: '0.95rem', fontWeight: 700, color: WHITE, whiteSpace: 'nowrap' }}>
                  {wageText(r)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgreementCard({ agreement, isMobile }) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const smallStyle = smallFor(isPhone);
  const lang = i18n.language;
  const { journey, foremen, other, apprentices, classSheet } = sectionRows(agreement.rows);
  const headline = journey[0] || null;
  // A class sheet (e.g. operating engineers' equipment classes) has no single journey rate: its
  // headline is the range across the classes.
  const classes = classSheet ? classRange(agreement.rows) : null;
  const rows = agreement.rows;

  // Agreement-level facts. Rows of one agreement normally share these; if a sheet ever
  // printed more than one, each is shown rather than silently picking one.
  const periods = [...new Map(rows.filter(r => r.effective_start || r.effective_end)
    .map(r => [`${r.effective_start}|${r.effective_end}`, r])).values()];
  const counties = distinct(rows, 'county_note');
  const dues = distinct(rows, 'dues_note');
  const sources = distinct(rows, 'source_url');
  const retrieved = distinct(rows, 'retrieved_date');

  const period = r => {
    const s = fmtDate(r.effective_start, lang), e = fmtDate(r.effective_end, lang);
    if (s && e) return t('unionWages.effectiveRange', { start: s, end: e });
    if (s) return t('unionWages.effectiveFrom', { start: s });
    return t('unionWages.effectiveThrough', { end: e });
  };

  return (
    <div data-agreement={agreement.agreement} style={{
      backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px',
      padding: isMobile ? '16px' : '22px', marginTop: '16px',
    }}>
      <p style={{ fontSize: isPhone ? '0.8rem' : '0.7rem', fontWeight: 700, color: AMBER, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>
        {agreement.trade}
      </p>
      <p style={{ fontSize: '1rem', fontWeight: 700, color: WHITE, lineHeight: 1.4 }}>{agreement.agreement}</p>
      {periods.map(r => (
        <p key={`${r.effective_start}|${r.effective_end}`} style={{ fontSize: isPhone ? PHONE_TEXT : '0.8rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.65)', marginTop: '4px' }}>{period(r)}</p>
      ))}
      {counties.map(c => (
        <p key={c} style={{ ...smallStyle, marginTop: '4px' }}>{t('unionWages.territory', { text: c })}</p>
      ))}

      {headline && fmtRate(headline.hourly_wage) && (
        <div data-headline style={{ marginTop: '16px', padding: '14px 16px', borderRadius: '10px', backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)' }}>
          <p style={{ fontSize: isPhone ? '0.75rem' : '0.66rem', fontWeight: 700, color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {t('unionWages.hourlyWage')} · {headline.level}
          </p>
          <p style={{ fontFamily: FONT_HEAD, fontSize: isMobile ? '2rem' : '2.4rem', color: WHITE, letterSpacing: '0.02em', lineHeight: 1.1 }}>
            {headline.is_estimate ? '~' : ''}{fmtRate(headline.hourly_wage)}
            <span style={{ fontFamily: FONT_BODY, fontSize: isPhone ? PHONE_TEXT : '0.85rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.55)', marginLeft: '6px' }}>{t('unionWages.perHour')}</span>
          </p>
          <BenefitsBlock row={headline} />
        </div>
      )}

      {!headline && classes && (
        <div data-class-range style={{ marginTop: '16px', padding: '14px 16px', borderRadius: '10px', backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)' }}>
          <p style={{ fontSize: isPhone ? '0.75rem' : '0.66rem', fontWeight: 700, color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {t('unionWages.hourlyWageRange')}
          </p>
          <p style={{ fontFamily: FONT_HEAD, fontSize: isMobile ? '2rem' : '2.4rem', color: WHITE, letterSpacing: '0.02em', lineHeight: 1.1 }}>
            {fmtRate(classes.min)} – {fmtRate(classes.max)}
            <span style={{ fontFamily: FONT_BODY, fontSize: isPhone ? PHONE_TEXT : '0.85rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.55)', marginLeft: '6px' }}>{t('unionWages.perHour')}</span>
          </p>
          <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.8rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.55)', marginTop: '2px' }}>
            {t('unionWages.classesCount', { count: classes.count })}
          </p>
        </div>
      )}

      {IDOL_AGREEMENT.test(agreement.agreement) && (
        <p data-idol-notice role="note" style={{
          marginTop: '16px', padding: '10px 14px', borderRadius: '10px', fontSize: isPhone ? PHONE_TEXT : '0.78rem', lineHeight: 1.5,
          color: 'rgba(255,255,255,0.75)', backgroundColor: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.3)',
        }}>
          {t('unionWages.idolNotice')}
        </p>
      )}

      {rows.some(r => r.is_estimate) && (
        <p data-estimate-notice role="note" style={{
          marginTop: '16px', padding: '10px 14px', borderRadius: '10px', fontSize: isPhone ? PHONE_TEXT : '0.78rem', lineHeight: 1.5,
          color: 'rgba(255,255,255,0.75)', backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)',
        }}>
          {t('unionWages.estimateNotice')}
        </p>
      )}

      <RateTable title={t('unionWages.sectionJourney')} rows={journey} isMobile={isMobile} skipBenefitsFor={headline} />
      <RateTable title={t('unionWages.sectionForemen')} rows={foremen} isMobile={isMobile} />
      <RateTable title={t(classSheet ? 'unionWages.sectionClasses' : 'unionWages.sectionOther')} rows={other} isMobile={isMobile} />
      <RateTable title={t('unionWages.sectionApprentices')} rows={apprentices} showPct isMobile={isMobile} />

      {dues.length > 0 && (
        <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.8rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.65)', lineHeight: 1.5, marginTop: '16px' }}>
          <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>{t('unionWages.dues')}: </span>{dues.join(' · ')}
        </p>
      )}

      <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        {sources.map(u => (
          <p key={u} style={smallStyle}>
            {t('unionWages.source')}{' '}
            <a href={u} target="_blank" rel="noopener noreferrer"
              style={isPhone ? { ...linkStyle, display: 'block', padding: '10px 0', minHeight: '44px' } : linkStyle}>{u}</a>
          </p>
        ))}
        {retrieved.map(d => (
          <p key={d} style={smallStyle}>{t('unionWages.retrieved', { date: fmtDate(d, lang) })}</p>
        ))}
        <p data-notice style={{ ...smallStyle, marginTop: '8px' }}>
          {t(IDOL_AGREEMENT.test(agreement.agreement) ? 'unionWages.noticeIdol' : 'unionWages.notice')}
        </p>
      </div>
    </div>
  );
}

const familyLabel = (fam, t) => (fam.labelKey ? t(fam.labelKey) : fam.label);

// Path for one local, opened on its main agreement in the given family.
function localPath(agreement, local) {
  const q = new URLSearchParams(agreement ? { local, agreement: agreement.agreement } : { local });
  return `/union-wages?${q.toString()}`;
}

// Hover/focus styling for the inline-styled links below.
function useHotState() {
  const [hot, setHot] = useState(false);
  const [focused, setFocused] = useState(false);
  return {
    active: hot || focused, focused,
    handlers: {
      onMouseEnter: () => setHot(true), onMouseLeave: () => setHot(false),
      onFocus: () => setFocused(true), onBlur: () => setFocused(false),
    },
  };
}

// One local: short name, a plain-language descriptor, and the headline hourly wage of its
// main agreement in this family (omitted when there isn't a single one). Links to its page.
function LocalRow({ local, summary, isFirst, compact }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const nameSize = isPhone ? '0.95rem' : '0.86rem';
  const subSize = isPhone ? PHONE_TEXT : '0.72rem';
  const subColor = isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.45)';
  const rowHeight = isPhone ? '64px' : ROW_HEIGHT;
  const { active, focused, handlers } = useHotState();
  const descKey = localDescriptorKey(local.name);
  const wage = fmtRate(summary.wage);
  return (
    <Link
      to={localPath(summary.agreement, local.name)}
      data-local={local.name}
      {...handlers}
      onClick={() => window.scrollTo(0, 0)}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none',
        height: compact ? rowHeight : 'auto', minHeight: rowHeight,
        padding: compact ? '0 8px' : '10px 16px', margin: compact ? '0 -8px' : 0, borderRadius: compact ? '8px' : 0,
        borderTop: isFirst || compact ? 'none' : '1px solid rgba(255,255,255,0.07)',
        backgroundColor: active ? 'rgba(245,166,35,0.08)' : 'transparent',
        outline: focused ? `2px solid ${AMBER}` : 'none', outlineOffset: '-2px',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p title={local.name} style={{ fontSize: nameSize, fontWeight: 700, color: active ? AMBER : WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {shortLocalName(local.name)}
        </p>
        <p style={{ fontSize: subSize, color: subColor, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {descKey ? t(descKey) : local.trades.join(' · ')}
        </p>
      </div>
      {wage && (
        <span data-headline-wage style={{ fontSize: nameSize, fontWeight: 700, color: WHITE, whiteSpace: 'nowrap' }}>
          {t('unionWages.perHourShort', { wage })}
        </span>
      )}
      {!wage && summary.range && (
        <span data-headline-range style={{ textAlign: 'right', flexShrink: 0 }}>
          <span style={{ display: 'block', fontSize: nameSize, fontWeight: 700, color: WHITE, whiteSpace: 'nowrap' }}>
            {t('unionWages.perHourShort', { wage: `${fmtRate(summary.range.min)} – ${fmtRate(summary.range.max)}` })}
          </span>
          <span style={{ display: 'block', fontSize: subSize, color: subColor, marginTop: '1px', whiteSpace: 'nowrap' }}>
            {summary.classCount
              ? t('unionWages.classesCount', { count: summary.classCount })
              : t('unionWages.tradesCount', { count: summary.tradeCount })}
          </span>
        </span>
      )}
    </Link>
  );
}

// Landing card for one trade family. Same structure on every card -- title, count, a
// fixed-height trade-name slot, then fixed-height local rows -- so rows line up across a grid
// row. Up to PREVIEW_LOCALS locals; the rest sit behind a "+N more" link to the family page.
function FamilyCard({ fam, rows }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const more = useHotState();
  const extra = fam.locals.length - PREVIEW_LOCALS;
  const label = familyLabel(fam, t);
  return (
    <div data-family={fam.key} style={{
      display: 'flex', flexDirection: 'column', minWidth: 0,
      backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '16px',
    }}>
      <p title={label} style={{ fontFamily: FONT_HEAD, fontSize: '1.25rem', color: WHITE, letterSpacing: '0.04em', lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </p>
      <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.74rem', fontWeight: 700, color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.45)', marginTop: '2px' }}>
        {t('unionWages.unionsCount', { count: fam.locals.length })}
      </p>
      <p style={{
        height: isPhone ? 'auto' : TRADES_SLOT, maxHeight: isPhone ? '2.6rem' : undefined, fontSize: isPhone ? PHONE_TEXT : '0.7rem',
        color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.4)', lineHeight: 1.45, marginTop: '6px',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {fam.trades.length > 1 ? fam.trades.join(' · ') : ''}
      </p>
      <div style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '4px' }}>
        {fam.locals.slice(0, PREVIEW_LOCALS).map(l => (
          <LocalRow key={l.name} local={l} summary={localSummary(rows, l.name, fam.key)} compact />
        ))}
        {extra > 0 && (
          <Link
            to={`/union-wages?family=${encodeURIComponent(fam.key)}`}
            data-more
            {...more.handlers}
            onClick={() => window.scrollTo(0, 0)}
            style={{
              display: 'flex', alignItems: 'center', height: isPhone ? '48px' : '40px', padding: '0 8px', margin: '0 -8px', borderRadius: '8px',
              textDecoration: 'none', fontSize: isPhone ? '0.95rem' : '0.8rem', fontWeight: 700, color: AMBER,
              backgroundColor: more.active ? 'rgba(245,166,35,0.08)' : 'transparent',
              outline: more.focused ? `2px solid ${AMBER}` : 'none', outlineOffset: '-2px',
            }}
          >
            {t('unionWages.moreUnions', { count: extra })}
          </Link>
        )}
      </div>
    </div>
  );
}

// Public page: union wage sheets as published by each local. Level 1 lists trades and the
// locals with a sheet for that trade; level 2 (?local=...&agreement=...) shows one local with
// an agreement selector. The URL holds the selection so any local or agreement can be linked.
export default function UnionWages() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const width = useViewportWidth();
  const isMobile = width < 768;
  const isPhone = useIsPhone();
  const columns = width >= 1024 ? 3 : width >= 640 ? 2 : 1;
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      body { background: ${NAVY}; font-family: 'DM Sans', sans-serif; margin: 0; }
      input::placeholder { color: rgba(255,255,255,0.28); }
      select option { background-color: ${NAVY_MID}; color: ${WHITE}; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const res = await fetchPublishedWageRates();
    setRows(res.rows);
    setLoadError(res.error);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const families = useMemo(() => {
    const list = groupByFamily(rows);
    return list.sort((a, b) => familyLabel(a, t).localeCompare(familyLabel(b, t)));
  }, [rows, t]);
  const localName = params.get('local');
  const familyKey = params.get('family');
  const family = families.find(f => f.key === familyKey) || null;

  // Search: a family whose name or trade names match keeps all its locals; otherwise only
  // the locals whose full or short name matches are kept, and families left empty drop out.
  const q = search.trim().toLowerCase();
  const shownFamilies = !q ? families : families.map(f => {
    const famHit = familyLabel(f, t).toLowerCase().includes(q) || f.trades.some(tr => tr.toLowerCase().includes(q));
    if (famHit) return f;
    const locals = f.locals.filter(l => l.name.toLowerCase().includes(q) || shortLocalName(l.name).toLowerCase().includes(q));
    return locals.length ? { ...f, locals } : null;
  }).filter(Boolean);
  const agreements = useMemo(() => (localName ? agreementsForLocal(rows, localName) : []), [rows, localName]);
  const selected = agreements.find(a => a.agreement === params.get('agreement')) || defaultAgreement(agreements);

  let body;
  if (loading) {
    body = <div style={muted}>{t('common.loading')}</div>;
  } else if (loadError) {
    body = (
      <div role="alert" style={{ ...muted, color: ERROR_TEXT }}>
        <p>{t('unionWages.loadError')}</p>
        <button type="button" onClick={load} style={{ marginTop: '12px', background: 'transparent', color: WHITE, border: '1px solid rgba(255,255,255,0.3)', borderRadius: '7px', padding: '7px 14px', fontSize: '0.8rem', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer' }}>
          {t('unionWages.retry')}
        </button>
      </div>
    );
  } else if (rows.length === 0) {
    body = <div data-empty style={muted}>{t('unionWages.empty')}</div>;
  } else if (localName) {
    body = (
      <div>
        <button type="button" onClick={() => setParams({})} style={backBtnStyle(isPhone)}>
          {t('unionWages.allTrades')}
        </button>
        <h3 style={{ fontFamily: FONT_HEAD, fontSize: '1.4rem', color: WHITE, letterSpacing: '0.04em', margin: '10px 0 0' }}>{localName}</h3>
        {agreements.length === 0 ? (
          <div style={muted}>{t('unionWages.localNotFound')}</div>
        ) : (
          <>
            <label style={{ display: 'block', marginTop: '12px' }}>
              <span style={{ display: 'block', fontSize: isPhone ? '0.75rem' : '0.66rem', fontWeight: 700, color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
                {t('unionWages.agreementLabel')}
              </span>
              <select
                data-agreement-select
                value={selected.agreement}
                onChange={e => setParams({ local: localName, agreement: e.target.value })}
                style={{ ...selStyle, width: isMobile ? '100%' : 'auto', ...(isPhone ? { minHeight: '44px', fontSize: '16px' } : {}) }}
              >
                {agreements.map(a => <option key={a.agreement} value={a.agreement}>{a.agreement}</option>)}
              </select>
            </label>
            <AgreementCard key={selected.agreement} agreement={selected} isMobile={isMobile} />
          </>
        )}
      </div>
    );
  } else if (familyKey) {
    body = (
      <div>
        <button type="button" onClick={() => setParams({})} style={backBtnStyle(isPhone)}>
          {t('unionWages.allTrades')}
        </button>
        {!family ? (
          <div style={muted}>{t('unionWages.familyNotFound')}</div>
        ) : (
          <>
            <h3 style={{ fontFamily: FONT_HEAD, fontSize: '1.4rem', color: WHITE, letterSpacing: '0.04em', margin: '10px 0 0' }}>{familyLabel(family, t)}</h3>
            <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.78rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.45)', marginTop: '2px' }}>{t('unionWages.unionsCount', { count: family.locals.length })}</p>
            <div style={{ marginTop: '14px', backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', overflow: 'hidden' }}>
              {family.locals.map((l, i) => (
                <LocalRow key={l.name} local={l} isFirst={i === 0} summary={localSummary(rows, l.name, family.key)} />
              ))}
            </div>
          </>
        )}
      </div>
    );
  } else {
    body = (
      <div>
        <input
          type="search"
          data-wage-search
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('unionWages.searchPlaceholder')}
          aria-label={t('unionWages.searchPlaceholder')}
          style={{ ...selStyle, cursor: 'text', display: 'block', width: '100%', marginBottom: '16px', fontSize: isMobile ? '16px' : '0.84rem', ...(isPhone ? { minHeight: '44px' } : {}) }}
        />
        {shownFamilies.length === 0 ? (
          <div style={muted}>{t('unionWages.noMatches')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, alignItems: 'stretch', gap: '12px' }}>
            {shownFamilies.map(f => <FamilyCard key={f.key} fam={f} rows={rows} />)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: NAVY }}>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100, height: '60px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
      }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={32} />
        </div>
        <LanguageToggle large={isPhone} />
      </nav>

      <div style={DIRECTORY_CONTAINER}>
        {/* Same heading and tab row as every other Trades Resource Directory tab. */}
        <DirectoryHeading />
        <ResourceTabs activeTab="unionWages" fullWidth />
        <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.7rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '6px' }}>
          {t('unionWages.title')}
        </h2>
        <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.8rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.45)', lineHeight: 1.5, marginBottom: '16px', maxWidth: '640px' }}>
          {t('unionWages.subtitle')}
        </p>
        {body}
      </div>
    </div>
  );
}
